import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetQuoteComments } from '~/api';
import { quoteTimelineRoundFragment } from '~/api/requestForQuote';
import { formatCurrency, formatDateTime } from '~/utils';
import { useFragment as unmaskRounds, type FragmentType } from '~/graphql';
import {
  QuoteRoundParty,
  type QuoteTimelineRoundFragment,
} from '~/graphql/graphql';

export type TimelineRound = QuoteTimelineRoundFragment;

type TimelineMoney = TimelineRound['pickupPriceForTally'];

type TimelineEntry = {
  key: string;
  kind: 'round' | 'comment';
  at: string;
  heading: string;
  authorName: string;
  detail: string;
};

type QuoteTimelineProps = Readonly<{
  quoteId: string;
  rounds: FragmentType<typeof quoteTimelineRoundFragment>[];
}>;

type Translate = (key: string) => string;

function roundHeading(round: TimelineRound, t: Translate) {
  const isSeller = round.party === QuoteRoundParty.Seller;
  if (round.declined) {
    return isSeller
      ? t('rfq_view.timeline_declined_seller')
      : t('rfq_view.timeline_declined_buyer');
  }
  return isSeller
    ? t('rfq_view.timeline_offer_seller')
    : t('rfq_view.timeline_offer_buyer');
}

// A round may carry a pickup price, a delivery price, or both; each one shown is labelled.
function labelledPrices(
  pickup: TimelineMoney,
  delivery: TimelineMoney,
  pickupLabel: string,
  deliveryLabel: string
): string | null {
  const parts = [
    pickup &&
      `${pickupLabel}: ${formatCurrency(pickup.amount, pickup.currency)}`,
    delivery &&
      `${deliveryLabel}: ${formatCurrency(delivery.amount, delivery.currency)}`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function roundDetail(round: TimelineRound, t: Translate) {
  if (round.declined) {
    return round.declineReason ?? '';
  }
  const tallyPrices = labelledPrices(
    round.pickupPriceForTally,
    round.deliveryPriceForTally,
    t('rfq_view.tally_pickup_price_label'),
    t('rfq_view.tally_delivery_price_label')
  );
  if (tallyPrices) {
    return tallyPrices;
  }
  return round.items
    .map((item) => {
      const displayValue = item.platformProductsLength.lengthDisplayValue;
      const prices = labelledPrices(
        item.pickupPrice,
        item.deliveryPrice,
        t('rfq_view.pickup_price_label'),
        t('rfq_view.delivery_price_label')
      );
      return prices ? `${displayValue} ${prices}` : displayValue;
    })
    .join(' · ');
}

export function QuoteTimeline({
  quoteId,
  rounds: maskedRounds,
}: QuoteTimelineProps) {
  const { t } = useTranslation('rfq-view');
  const rounds = unmaskRounds(quoteTimelineRoundFragment, maskedRounds);
  const { data, isLoading, isError } = useGetQuoteComments(quoteId);
  const comments = data?.quoteComments;

  // The parent keeps the comment draft in its state, so this re-renders on every keystroke.
  const entries = useMemo<TimelineEntry[]>(
    () =>
      [
        ...rounds.map((round) => ({
          key: `round-${round.roundNumber}`,
          kind: 'round' as const,
          at: round.createdAt,
          heading: roundHeading(round, t),
          authorName: round.authorName,
          detail: roundDetail(round, t),
        })),
        ...(comments ?? []).map((comment) => ({
          key: `comment-${comment.id}`,
          kind: 'comment' as const,
          at: comment.createdAt,
          heading: t('rfq_view.timeline_comment'),
          authorName: comment.authorName,
          detail: comment.body,
        })),
      ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()),
    [rounds, comments, t]
  );

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-semibold text-neut-prim">
        {t('rfq_view.timeline_heading')}
      </h4>

      {isError && (
        <p className="text-sm text-error-600" role="alert">
          {t('rfq_view.conversation_load_failed')}
        </p>
      )}

      {!isError && !isLoading && entries.length === 0 && (
        <p className="text-sm text-neut-sec">{t('rfq_view.timeline_empty')}</p>
      )}

      {entries.map((entry) => (
        <div
          key={entry.key}
          data-testid="quote-timeline-entry"
          data-kind={entry.kind}
          className="flex flex-col gap-0.5"
        >
          <p className="text-xs text-neut-sec">
            {[entry.heading, entry.authorName, formatDateTime(entry.at)]
              .filter((part) => part.length > 0)
              .join(' · ')}
          </p>
          {entry.detail.length > 0 && (
            <p className="text-sm text-neut-prim">{entry.detail}</p>
          )}
        </div>
      ))}
    </div>
  );
}
